import React, { useEffect, useState, useCallback } from "react";
import { getQrCodes, createQrCodeRange, markQrCodesUsed, getQrCodesReport } from "../../api/index";
import { excelQrCodesReport } from "../exportToExcel";
import AlertSuccess from "../../components/AlertSuccess";
import AlertDenger from "../../components/AlertDenger";
import Button from "../../components/Button";
import Pagination from "../Table/Pagination";
import UNAUTHORIZED from "../UNAUTHORIZED";

const PREFIX = "AM";
const QR_PAGE_USERS = ["admin", "asmaa", "abdelrhman"];

function QrCodes() {
  const userProfile = JSON.parse(localStorage.getItem("profile"))?.value?.[0];
  const canAccess = QR_PAGE_USERS.includes(userProfile?.username);

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ total: 0, used: 0, remaining: 0, lastUsedCode: null });

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [lastUsedInput, setLastUsedInput] = useState("");
  const [usedDate, setUsedDate] = useState("");
  const [markingUsed, setMarkingUsed] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const flash = (type, msg) => {
    if (type === "success") {
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(""), 3000);
    } else {
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(""), 3500);
    }
  };

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getQrCodes(page, search);
      setCodes(data.data || []);
      setMaxPage(data.count?.maxPage || 1);
      setTotal(data.count?.total || 0);
      if (data.summary) setSummary(data.summary);
    } catch {
      flash("error", "Failed to load QR codes.");
    }
    setLoading(false);
  }, [page, search]);

  useEffect(() => {
    if (!canAccess) return;
    fetchCodes();
  }, [fetchCodes, canAccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (start === "" || end === "") {
      flash("error", "Please enter both a start and end number.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createQrCodeRange(Number(start), Number(end));
      const { total: createdTotal, inserted, skipped } = res.data;
      flash(
        "success",
        `Added ${inserted} of ${createdTotal} codes${
          skipped > 0 ? ` (${skipped} already existed and were skipped)` : ""
        }.`
      );
      setStart("");
      setEnd("");
      setPage(1);
      await fetchCodes();
    } catch (err) {
      flash("error", err?.response?.data?.ERROR || "Failed to add QR code range.");
    }
    setSubmitting(false);
  };

  const handleMarkUsed = async (e) => {
    e.preventDefault();
    if (lastUsedInput.trim() === "" || !usedDate) {
      flash("error", "Please enter both the last used code and a using date.");
      return;
    }
    setMarkingUsed(true);
    try {
      const res = await markQrCodesUsed(lastUsedInput.trim(), usedDate);
      const { newlyMarked, totalUsed, total: totalCodes, lastUsedCode } = res.data;
      flash(
        "success",
        newlyMarked > 0
          ? `Marked ${newlyMarked} code(s) as used, up to ${lastUsedCode}. Total used: ${totalUsed}/${totalCodes}.`
          : `No unused codes found up to that number.`
      );
      await fetchCodes();
    } catch (err) {
      flash("error", err?.response?.data?.ERROR || "Failed to mark codes as used.");
    }
    setMarkingUsed(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await getQrCodesReport();
      await excelQrCodesReport(data.summary, data.usedByDate);
    } catch {
      flash("error", "Failed to export Excel file.");
    }
    setExporting(false);
  };

  if (!canAccess) return <UNAUTHORIZED />;

  return (
    <div className="p-5">
      <div className="flex items-center justify-between pb-5 flex-wrap gap-3">
        <h1 className="text-2xl text-white pl-1">QR Codes</h1>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
        >
          📊 {exporting ? "Exporting..." : "Export Excel"}
        </button>
      </div>

      {successMsg && <AlertSuccess text={successMsg} />}
      {errorMsg && <AlertDenger text={errorMsg} />}

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-800">{summary.total}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <p className="text-sm text-gray-500">Used</p>
          <p className="text-2xl font-bold text-amber-600">{summary.used}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <p className="text-sm text-gray-500">Remaining</p>
          <p className="text-2xl font-bold text-green-600">{summary.remaining}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <p className="text-sm text-gray-500">Last Used Code</p>
          <p className="text-2xl font-bold text-blue-600">{summary.lastUsedCode || "—"}</p>
        </div>
      </div>

      {/* Add range form */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Add QR Code Range</h2>
        <form onSubmit={handleSubmit} className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col min-w-24">
            <label className="text-sm font-medium mb-1">Prefix</label>
            <input
              type="text"
              value={PREFIX}
              disabled
              className="border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-500"
            />
          </div>
          <div className="flex flex-col flex-1 min-w-40">
            <label className="text-sm font-medium mb-1">From Number *</label>
            <input
              type="number"
              min="0"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              placeholder="e.g. 500"
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            />
          </div>
          <div className="flex flex-col flex-1 min-w-40">
            <label className="text-sm font-medium mb-1">To Number *</label>
            <input
              type="number"
              min="0"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              placeholder="e.g. 1000"
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            color="bg-green-600 hover:bg-green-700"
            text={submitting ? "Adding..." : "Add Range"}
          />
        </form>
        <p className="text-xs text-gray-500 mt-2">
          Example: From 50960 to 60960 creates {PREFIX}50960, {PREFIX}50961, ... {PREFIX}60960.
          Codes that already exist are skipped automatically.
        </p>
      </div>

      {/* Mark as used form */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Mark QR Codes as Used</h2>
        <form onSubmit={handleMarkUsed} className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col flex-1 min-w-40">
            <label className="text-sm font-medium mb-1">Last Used Code *</label>
            <input
              type="text"
              value={lastUsedInput}
              onChange={(e) => setLastUsedInput(e.target.value)}
              placeholder="e.g. AM50960 or 50960"
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
              required
            />
          </div>
          <div className="flex flex-col flex-1 min-w-40">
            <label className="text-sm font-medium mb-1">Using Date *</label>
            <input
              type="date"
              value={usedDate}
              onChange={(e) => setUsedDate(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={markingUsed}
            color="bg-amber-600 hover:bg-amber-700"
            text={markingUsed ? "Marking..." : "Mark as Used"}
          />
        </form>
        <p className="text-xs text-gray-500 mt-2">
          Enter the last code used (e.g. AM50960 or just 50960) and every still-unused
          code from the first available number through it will be marked used on the
          given date. Codes already marked used are left untouched, so this is safe to
          run again later with a higher number as more get used.
        </p>
      </div>

      {/* List */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-xl font-semibold">
            All QR Codes{" "}
            <span className="text-gray-500 text-base font-normal">({total})</span>
          </h2>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            placeholder="Search by code..."
            className="border border-gray-300 rounded-md px-3 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {loading && <p className="text-gray-500 text-center py-6">Loading...</p>}

        {!loading && codes.length === 0 && (
          <p className="text-gray-400 text-center py-6">No QR codes found.</p>
        )}

        {!loading && codes.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="px-4 py-2 border border-gray-200">Code</th>
                  <th className="px-4 py-2 border border-gray-200 text-center">Used</th>
                  <th className="px-4 py-2 border border-gray-200">Used Date</th>
                  <th className="px-4 py-2 border border-gray-200">Created By</th>
                  <th className="px-4 py-2 border border-gray-200">Created At</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr
                    key={c.id}
                    className={`hover:bg-blue-50 transition-colors ${
                      Number(c.used) === 1 ? "bg-amber-50" : ""
                    }`}
                  >
                    <td className="px-4 py-2 border border-gray-200 font-medium">{c.code}</td>
                    <td className="px-4 py-2 border border-gray-200 text-center">
                      {Number(c.used) === 1 ? (
                        <span className="inline-block px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                          Used
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 border border-gray-200">{c.used_date || "—"}</td>
                    <td className="px-4 py-2 border border-gray-200">{c.created_by || "—"}</td>
                    <td className="px-4 py-2 border border-gray-200">
                      {c.created_at ? new Date(c.created_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="py-4">
          <Pagination
            page={page}
            setPage={setPage}
            maxPage={maxPage}
            nextDisabled={page >= maxPage}
          />
        </div>
      </div>
    </div>
  );
}

export default QrCodes;
